import {
  Badge,
  Button,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useMemo, useState } from 'react';

import { notificationsApi } from 'apis/notifications';
import { TopbarButton } from 'app/views/layout/components/layoutContainer/components/topBar/components/button/topbarButton';
import { AppLink } from 'components/appLink';
import { notificationActions } from 'components/notifications/actions/notification';
import { transformAndSortNotifications } from 'components/notifications/components/notificationsPage';
import { useMentionAuthorLookup } from 'components/notifications/hooks/useMentionAuthorLookup';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { MentionNotificationHistoryItem } from './mentionNotificationHistoryItem';
import { NotificationHistoryItem } from './notificationHistoryItem';
import styles from './notificationTopbarButton.module.scss';

const numberOfVisibleNotifications = 3;

export const NotificationTopbarButton = memo(
  function NotificationTopbarButton(): JSX.Element {
    const dispatch = useAppDispatch();
    const [isOpen, setIsOpen] = useState(false);

    const [updateNotificationsAsRead] =
      notificationsApi.useLazyUpdateNotificationsAsReadQuery();

    const { unreadNotifications, isFetching } =
      notificationsApi.useReadNotificationsQuery(
        {
          unreadOnly: false,
        },
        {
          refetchOnMountOrArgChange: false,
          selectFromResult: ({ data, ...rest }) => ({
            unreadNotifications:
              data?.data.notifications.filter(({ is_read }) => !is_read) ?? [],
            ...rest,
          }),
        },
      );

    const authorsById = useMentionAuthorLookup(unreadNotifications);

    const notifications = useMemo(
      () => transformAndSortNotifications(unreadNotifications, authorsById),
      [unreadNotifications, authorsById],
    );

    const onReset = useCallback(async () => {
      const notificationIds = notifications
        .filter(
          ({ payload }) => payload?.type !== 'local_notification:displayed',
        )
        .map(({ id }) => id);

      if (notificationIds.length) {
        // Set is_read for API notifications.
        updateNotificationsAsRead({
          notificationIds,
        });
      }

      // Set is_read for local notifications.
      dispatch(notificationActions.notificationHistoryReset());
    }, [dispatch, notifications, updateNotificationsAsRead]);

    const onToggle = useCallback((open: boolean) => {
      setIsOpen(open);
      if (open) {
        ampli.topbarNotificationsOpened();
      }
    }, []);

    const visibleNotifications = useMemo(
      () =>
        notifications
          .slice(-numberOfVisibleNotifications) // Last n elements
          .reverse(),
      [notifications],
    );

    const notVisibleNotificationsNumber =
      notifications.length - visibleNotifications.length;

    return (
      <Popover.Root open={isOpen} onOpenChange={onToggle}>
        <Popover.Trigger asChild>
          <TopbarButton
            icon="bell"
            label="Notifications"
            notification={
              notifications.length > 0 ? notifications.length : undefined
            }
          />
        </Popover.Trigger>

        <Popover.Content
          className={styles.popover}
          side="bottom"
          align="end"
          sideOffset={parseInt(sizes.spacer.values[100])}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Columns>
            <Column className={styles.content}>
              <Column.Row slottable borderBottom>
                <Column.Slot placement="start">
                  <Text.Heading>Notifications</Text.Heading>
                </Column.Slot>

                <Column.Slot placement="end">
                  {visibleNotifications.length > 0 && (
                    <Button
                      disabled={isFetching}
                      variant="tertiary"
                      onClick={onReset}
                    >
                      Mark all as seen
                    </Button>
                  )}

                  <Badge size="small" variant="info">
                    {notifications.length}
                  </Badge>
                </Column.Slot>
              </Column.Row>

              <Column.Row
                scrollable
                noHorizontalPadding
                noVerticalPadding={visibleNotifications.length > 0}
              >
                {visibleNotifications.length ? (
                  visibleNotifications.map((notification) => {
                    if (
                      notification.payload?.type === 'mention:user_mentioned'
                    ) {
                      return (
                        <MentionNotificationHistoryItem
                          key={notification.id}
                          notification={notification}
                          onActionHandled={() => setIsOpen(false)}
                          userMentionedData={notification.payload.params}
                        />
                      );
                    }
                    return (
                      <NotificationHistoryItem
                        key={notification.id}
                        notification={notification}
                      />
                    );
                  })
                ) : isFetching ? (
                  <FlexBox
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Spinner size={32} />
                  </FlexBox>
                ) : (
                  <EmptyState
                    title="No notifications"
                    description="There are no unread notifications"
                    icon={null}
                  />
                )}
              </Column.Row>

              <Column.Row slottable noMinHeight borderTop>
                <Column.Slot placement="center">
                  <AppLink
                    path="/notifications"
                    onClick={() => setIsOpen(false)}
                  >
                    {notVisibleNotificationsNumber > 0
                      ? `See all notifications (${notVisibleNotificationsNumber} more new)`
                      : 'See all notifications'}
                  </AppLink>
                </Column.Slot>
              </Column.Row>
            </Column>
          </Columns>
        </Popover.Content>
      </Popover.Root>
    );
  },
);
